import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '../../hooks/useDebounce';
import { useTheme } from '../../theme';
import { LeaderboardSearchBar } from './LeaderboardSearchBar';
import { LeaderboardTable } from './LeaderboardTable';
import type { LeaderboardRow, LeaderboardStats } from './types';

const SEARCH_DEBOUNCE_MS = 400;

export interface LeaderboardManagementProps {
  stats: LeaderboardStats;
  rows: LeaderboardRow[];
  loading?: boolean;
  /** Paginação servidor (sem filtro local). */
  pagination?: {
    totalRecords: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
  };
  /** Busca controlada pelo pai (API). */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  /** Sem container de fundo/padding — quando a página já envolve o conteúdo (ex. b2b LeaderboardPage). */
  omitPageShell?: boolean;
}

function matchesSearch(row: LeaderboardRow, q: string): boolean {
  if (!q.trim()) return true;
  const s = q.trim().toLowerCase();
  return (
    row.name.toLowerCase().includes(s) ||
    row.email.toLowerCase().includes(s) ||
    row.userId.toLowerCase().includes(s)
  );
}

export function LeaderboardManagement({
  stats,
  rows,
  loading = false,
  pagination,
  searchValue: searchValueProp,
  onSearchChange,
  omitPageShell = false,
}: LeaderboardManagementProps) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const lb = theme.palette.adminLeaderboard;

  const [internalSearch, setInternalSearch] = useState('');
  const isControlledSearch = onSearchChange != null;
  const searchInput = isControlledSearch
    ? (searchValueProp ?? '')
    : internalSearch;
  const setSearchInput = isControlledSearch
    ? onSearchChange
    : setInternalSearch;

  const isServerPagination = pagination != null;
  const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);

  const filteredRows = useMemo(() => {
    if (isServerPagination) return rows;
    const q = debouncedSearch;
    return rows.filter(r => matchesSearch(r, q));
  }, [rows, debouncedSearch, isServerPagination]);

  const numFmt = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language === 'es' ? 'es' : 'en-US', {
        maximumFractionDigits: 0,
      }),
    [i18n.language]
  );

  const pageCount =
    pagination && pagination.pageSize > 0
      ? Math.max(1, Math.ceil(pagination.totalRecords / pagination.pageSize))
      : 1;

  const statIcon = (src: string) => (
    <img
      src={src}
      alt=""
      width={lb.statIconImageSize}
      height={lb.statIconImageSize}
      style={{ display: 'block', objectFit: 'contain' }}
      aria-hidden
    />
  );

  const paginationStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.md,
    flexWrap: 'wrap' as const,
    gap: theme.spacing.md,
    borderTop: `${theme.shape.borderWidth.thin} solid ${lb.cardBorder}`,
    backgroundColor: lb.cardBackground,
  };

  const buttonClickStyle = `
    .leaderboard-admin-btn { transition: transform 0.12s ease, opacity 0.12s ease, background-color 0.12s ease; }
    .leaderboard-admin-btn:active { transform: scale(0.97); }
  `;

  const shellStyle = {
    boxSizing: 'border-box' as const,
    width: '100%',
    minWidth: 0,
    minHeight: '100%',
    backgroundColor: lb.pageBackground,
    padding: theme.spacing.xl,
  };

  const inner = (
    <>
      <style>{buttonClickStyle}</style>

      {/* <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: lb.statCardsGap,
          marginBottom: lb.sectionGapAfterStats,
        }}
      >
        <LeaderboardStatCard
          label={t('leaderboard.stats.totalActive')}
          value={numFmt.format(stats.totalActiveUsers)}
          icon={statIcon(iconLeaderboardTotalActive)}
          variant="default"
        />
        <LeaderboardStatCard
          label={t('leaderboard.stats.avgPoints')}
          value={numFmt.format(stats.avgPointsPerUser)}
          icon={statIcon(iconLeaderboardAvgPoints)}
          variant="points"
        />
        <LeaderboardStatCard
          label={t('leaderboard.stats.coursesCompleted')}
          value={numFmt.format(stats.coursesCompleted)}
          icon={statIcon(iconLeaderboardCoursesCompleted)}
          variant="default"
          valueAlign="center"
        />
        <LeaderboardStatCard
          label={t('leaderboard.stats.engagementRate')}
          value={`${numFmt.format(stats.engagementRatePercent)}%`}
          icon={statIcon(iconLeaderboardEngagementRate)}
          variant="engagement"
        />
      </div> */}

      <div style={{ marginBottom: theme.spacing.lg }}>
        <LeaderboardSearchBar value={searchInput} onChange={setSearchInput} />
      </div>

      <div
        style={{
          borderRadius: lb.cardBorderRadius,
          border: `${theme.shape.borderWidth.thin} solid ${lb.cardBorder}`,
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div
            style={{
              padding: theme.spacing['2xl'],
              textAlign: 'center',
              color: lb.subtitleText,
              fontSize: theme.typography.fontSize.base,
            }}
          >
            {t('leaderboard.loading')}
          </div>
        ) : (
          <>
            <LeaderboardTable rows={filteredRows} />
            {pagination && (
              <div style={paginationStyle}>
                <span
                  style={{
                    fontSize: theme.typography.fontSize.sm,
                    color: lb.subtitleText,
                  }}
                >
                  {t('leaderboard.pagination.summary', {
                    count: filteredRows.length,
                    total: pagination.totalRecords,
                  })}
                </span>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing.sm,
                    flexWrap: 'wrap',
                  }}
                >
                  <button
                    type="button"
                    className="leaderboard-admin-btn"
                    disabled={pagination.page <= 1}
                    onClick={() =>
                      pagination.onPageChange(Math.max(1, pagination.page - 1))
                    }
                    style={{
                      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                      fontSize: theme.typography.fontSize.sm,
                      fontFamily: theme.typography.fontFamily,
                      border: `${theme.shape.borderWidth.thin} solid ${lb.cardBorder}`,
                      borderRadius: theme.shape.borderRadius.sm,
                      backgroundColor: lb.cardBackground,
                      color: lb.subtitleText,
                      cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer',
                      opacity: pagination.page <= 1 ? 0.6 : 1,
                    }}
                  >
                    {t('leaderboard.pagination.previous')}
                  </button>
                  {Array.from({ length: pageCount }, (_, i) => i + 1).map(
                    pageNum => {
                      const isActive = pagination.page === pageNum;
                      return (
                        <button
                          key={pageNum}
                          type="button"
                          className="leaderboard-admin-btn"
                          onClick={() => pagination.onPageChange(pageNum)}
                          style={{
                            minWidth: 32,
                            padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                            fontSize: theme.typography.fontSize.sm,
                            fontFamily: theme.typography.fontFamily,
                            border: isActive
                              ? 'none'
                              : `${theme.shape.borderWidth.thin} solid ${lb.cardBorder}`,
                            borderRadius: theme.shape.borderRadius.sm,
                            backgroundColor: isActive
                              ? theme.palette.primary.main
                              : lb.cardBackground,
                            color: isActive
                              ? theme.palette.primary.contrastText
                              : lb.titleText,
                            cursor: 'pointer',
                          }}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}
                  <button
                    type="button"
                    className="leaderboard-admin-btn"
                    disabled={pagination.page >= pageCount}
                    onClick={() =>
                      pagination.onPageChange(
                        Math.min(pageCount, pagination.page + 1)
                      )
                    }
                    style={{
                      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                      fontSize: theme.typography.fontSize.sm,
                      fontFamily: theme.typography.fontFamily,
                      border: `${theme.shape.borderWidth.thin} solid ${lb.cardBorder}`,
                      borderRadius: theme.shape.borderRadius.sm,
                      backgroundColor: lb.cardBackground,
                      color: lb.titleText,
                      cursor:
                        pagination.page >= pageCount
                          ? 'not-allowed'
                          : 'pointer',
                      opacity: pagination.page >= pageCount ? 0.6 : 1,
                    }}
                  >
                    {t('leaderboard.pagination.next')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  return omitPageShell ? (
    inner
  ) : (
    <div style={shellStyle}>{inner}</div>
  );
}
